import { Canvas, useFrame } from '@react-three/fiber'
import { useGLTF, RoundedBox, MeshTransmissionMaterial, CameraControls, Environment, Preload } from '@react-three/drei'
import { useRef, useState, Suspense } from 'react'

const CameraControlsWrapper = () => {
  // Detect if device is mobile/touch
  const isMobileDevice = () => {
    const hasTouchPoints = navigator.maxTouchPoints && navigator.maxTouchPoints > 0
    const userAgentTest = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    return hasTouchPoints || userAgentTest
  }

  const isMobile = isMobileDevice()

  return (
    <CameraControls
      minDistance={5}
      maxDistance={5}
      enableZoom={false}
      enablePan={false}
      mouseButtons={{
        left: isMobile ? 0 : 1, // Disable on mobile, enable rotation on desktop
        middle: 0, // Disabled
        right: 0, // Disabled
        wheel: 0  // Disable scroll wheel
      }}
      touches={{
        one: isMobile ? 0 : 8,   // Disable touch on mobile
        two: 0,                   // Disable two-finger zoom
        three: 0                  // Disable three-finger pan
      }}
    />
  )
}

/*
Auto-generated by: https://github.com/pmndrs/gltfjsx
Command: npx gltfjsx@6.2.13 earth.glb --transform --simplify 
Files: earth.glb [9.73MB] > earth-transformed.glb [172KB] (98%)
Author: PatelDev (https://sketchfab.com/PatelDev)
License: CC-BY-4.0 (http://creativecommons.org/licenses/by/4.0/)
Source: https://sketchfab.com/3d-models/earth-f7a76c63ff1846afb2d606e5c8369c15
Title: Earth
*/

function Earth(props) {
  const ref = useRef()
  const { nodes, materials } = useGLTF('/earth-transformed.glb')

  return (
    <group ref={ref} {...props} dispose={null} scale={0.2} position={[0, -0.6, 0]}>
      {Object.keys(nodes).map((nodeName) => {
        const node = nodes[nodeName]

        if (!node.isMesh && !node.isGroup) return null

        if (node.isMesh) {
          let xOffset = 0, yOffset = 0, zOffset = 0
          if (nodeName === 'Cube001') {
            xOffset = 0     // Move left/right
            yOffset = 2.2   // Move up/down
            zOffset = 1     // Move forward/back
          }
          return (
            <mesh
              key={nodeName}
              castShadow
              geometry={node.geometry}
              material={node.material || materials[Object.keys(materials)[0]]}
              position={[node.position.x + xOffset, node.position.y + yOffset, node.position.z + zOffset]}
              rotation={node.rotation}
              scale={node.scale}
            />
          )
        }

        return null
      })}
    </group>
  )
}

function CubeGroup({ mousePos, isMobile }) {
  const groupRef = useRef()
  const velocityRef = useRef({ x: 0, y: 0 })

  useFrame(() => {
    if (groupRef.current) {
      // Desktop: Accumulate velocity from mouse movement
      // Mobile: Add constant auto-rotation
      if (isMobile) {
        // Auto-rotate on mobile at a constant speed
        velocityRef.current.x = 0.002 // Constant horizontal rotation
        velocityRef.current.y = 0.0005 // Slight vertical rotation
      } else {
        // Desktop: Continuously accumulate velocity from mouse movement
        velocityRef.current.x += mousePos.x * 0.00005
        velocityRef.current.y += mousePos.y * 0.00005

        // Apply damping for smooth deceleration
        velocityRef.current.x *= 0.98
        velocityRef.current.y *= 0.98
      }

      // Update rotation with velocity
      groupRef.current.rotation.y += velocityRef.current.x
      groupRef.current.rotation.x += velocityRef.current.y
    }
  })

  return (
    <group ref={groupRef} position={[0, 0.5, 0]}>
      <RoundedBox castShadow scale={2.1}>
        <MeshTransmissionMaterial
          backside
          backsideThickness={-1}
          thickness={0.2}
          anisotropicBlur={0.02}
        />
      </RoundedBox>
      <Earth scale={0.7} position={[0, 0, 0]} />
    </group>
  )
}

function LoadingSpinner() {
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'transparent',
        zIndex: 1000,
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(0, 212, 255, 0.3)',
            borderTop: '4px solid #00d4ff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px',
          }}
        />
        <p style={{ color: '#00d4ff', fontSize: '16px', fontFamily: 'Arial, sans-serif' }}>
          Loading 3D Scene...
        </p>
      </div>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}

export default function Viewer(props = {}) {
  const {
    height = '600px',
    sensitivity = 1,
    modelPath = '/earth-transformed.glb',
    containerStyle = {}
  } = props

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })
  const [isLoading, setIsLoading] = useState(true)

  // Detect if device is mobile/touch
  const isMobileDevice = () => {
    const hasTouchPoints = navigator.maxTouchPoints && navigator.maxTouchPoints > 0
    const userAgentTest = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    const result = hasTouchPoints || userAgentTest
    console.log('Mobile detection:', { hasTouchPoints, userAgentTest, isMobile: result })
    return result
  }

  const isMobile = isMobileDevice()

  const handleMouseMove = (e) => {
    // Disable mouse tracking on mobile devices
    if (isMobile) return

    // Normalize mouse position to [-1, 1] range based on window
    const x = (e.clientX / window.innerWidth) * 2 - 1
    const y = -(e.clientY / window.innerHeight) * 2 + 1

    setMousePos({ x: x * sensitivity, y: y * sensitivity })
  }

  const containerHeightStyle = typeof height === 'string' ? height : `${height}px`

  return (
    <div
      onMouseMove={isMobile ? undefined : handleMouseMove}
      style={{
        width: '100%',
        height: containerHeightStyle,
        margin: 0,
        padding: 0,
        background: 'transparent',
        position: 'relative',
        ...containerStyle
      }}
    >
      {isLoading && <LoadingSpinner />}
      <Suspense fallback={<LoadingSpinner />}>
        <Canvas
          shadows
          camera={{ position: [5, 2, 0], fov: 55 }}
          style={{
            background: 'transparent',
            width: '100%',
            height: '100%',
            pointerEvents: isMobile ? 'none' : 'auto'
          }}
          gl={{ alpha: true, antialias: true, preserveDrawingBuffer: true }}
          onCreated={() => setIsLoading(false)}
        >
          <CameraControlsWrapper />
          <CubeGroup mousePos={mousePos} isMobile={isMobile} />
          <Environment files="https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/1k/dancing_hall_1k.hdr" background={false} blur={1} />
          <Preload all />
        </Canvas>
      </Suspense>
    </div>
  )
}
